// src/views/UserOrders/UserOrders.jsx
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import ProfileMenu from "../../components/ProfileMenu/ProfileMenu";
import { useSEO } from "../../context/SEOContext";
import { useTranslation } from "react-i18next";

const UserOrders = () => {
  const { t } = useTranslation();
  const setSeo = useSEO();

  useEffect(() => {
    setSeo({
      title: t("orders.meta.title"),
      description: t("orders.meta.description"),
      keywords: t("orders.meta.keywords"),
      favicon: "/faviconnew.png",
    });
  }, [setSeo, t]);

  const { id } = useParams();
  const userId = sessionStorage.getItem("user_id");
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    if (userId !== id) {
      Swal.fire({
        icon: "warning",
        title: "Unauthorized Access!",
        text: "You cannot view another user's orders.",
      }).then(() => navigate(`/profile/${userId}`));
      return;
    }

    fetch(`${process.env.REACT_APP_API_URL}/orders?user_id=${userId}`)
      .then((r) => r.json())
      .then((data) => setOrders(Array.isArray(data) ? data : []))
      .catch(() => setOrders([]));
  }, [id, userId, navigate]);

  const formatDateTime = (datetime) => {
    const d = new Date(datetime);
    const hh = String(d.getHours()).padStart(2, "0");
    const mm = String(d.getMinutes()).padStart(2, "0");
    const DD = String(d.getDate()).padStart(2, "0");
    const MM = String(d.getMonth() + 1).padStart(2, "0");
    const YYYY = d.getFullYear();
    return `${hh}:${mm} / ${DD}.${MM}.${YYYY}`;
  };

  // TRY için 3, diğerleri için 0 ondalık hane
  const fmt = (amount, currRaw) => {
    const curr = String(currRaw || "TRY").toUpperCase();
    const isTRY = curr === "TRY" || curr === "TL";
    return formatMoney(Number(amount), isTRY ? "TRY" : curr, {
      min: isTRY ? 0 : 0,
      max: isTRY ? 0 : 0,
    });
  };

  const localizedColor = (item) => {
    const selected = item?.selectedVariation?.name;
    const def = item?.color?.split(",")[0]?.trim();
    const raw =
      selected && selected !== "Default" ? selected.trim() : def || "";
    if (!raw) return "";
    const normalized = raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
    return t(normalized);
  };

  const formatMoney = (amount, ccy) => {
    const n = Number(amount || 0);
    const code = (ccy || "TRY").toUpperCase() === "TL" ? "TRY" : ccy || "TRY";
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: code,
        minimumFractionDigits: code === "TRY" ? 0 : 0,
        maximumFractionDigits: code === "TRY" ? 0 : 0,
      }).format(n);
    } catch {
      return `${code} ${n.toFixed(code === "TRY" ? 0 : 0)}`;
    }
  };

  const pickLineAmount = (item, order, items) => {
    const ccy = (order?.currency || "TRY").toUpperCase();
    const qty = Number(item.quantity || 1);

    // 1) Aynı para birimiyle kaydedilmişse
    if (item.currency && String(item.currency).toUpperCase() === ccy) {
      if (item.line_total != null) return Number(item.line_total);
      if (item.unit_price != null) return Number(item.unit_price) * qty;
    }

    // 2) Tek ürünse: toplamı satıra yaz (en güvenlisi)
    if ((items?.length || 0) === 1) {
      return Number(order.total_price || 0);
    }

    // 3) Çok ürünse: TRY alanlarından oranlayarak dağıt
    const list = items || [];
    const sumTry = list.reduce(
      (s, it) =>
        s +
        Number(
          it.line_total_try ??
            it.price_try ??
            (it.currency?.toUpperCase() === "TRY"
              ? it.line_total ?? it.price ?? 0
              : 0)
        ),
      0
    );
    if (sumTry > 0) {
      const selfTry =
        Number(item.line_total_try ?? item.price_try ?? 0) ||
        (item.currency?.toUpperCase() === "TRY"
          ? Number(item.line_total ?? item.price ?? 0)
          : 0);
      const ratio = selfTry / sumTry;
      return Number(order.total_price || 0) * ratio;
    }

    // 4) Son çare: elde ne varsa
    if (item.line_total != null) return Number(item.line_total);
    if (item.price != null) return Number(item.price);
    if (item.unit_price != null) return Number(item.unit_price) * qty;

    return 0;
  };

  return (
    <>
      <ProfileMenu userId={userId} />
      <div className="container py-5">
        <div className="row">
          <div className="col-lg-12">
            <h2 className="mb-5 cardtitlecolor">{t("OrdersTitle")}</h2>

            {orders.length > 0 ? (
              <div className="row">
                {orders.map((order) => {
                  const address =
                    typeof order.shipping_address === "string"
                      ? JSON.parse(order.shipping_address || "{}")
                      : order.shipping_address || {};

                  const couponCode = order?.coupon_code ?? null;
                  const couponDiscount = Number(order?.coupon_discount ?? 0);
                  const couponUsed = !!couponCode && couponDiscount > 0;
                  return (
                    <div className="col-lg-12 mb-3" key={order.id}>
                      <div className="cardborder p-4 rounded-3">
                        <div className="row p-4">
                          <div className="col-lg-6 mb-3">
                            <h5 className="mb-4 cardtitlecolor">
                              {t("BillingAddress")}
                            </h5>

                            <span className="text-muted">
                              <i className="fa-regular fa-envelope text-muted me-1" />
                              {t("EmailLabel")}
                            </span>
                            <h6 className="fw-normal mt-2 mb-3">
                              {order.email}
                            </h6>

                            <span className="text-muted">
                              <i className="fas fa-mobile-alt text-muted me-1" />
                              {t("PhoneNumberLabel")}
                            </span>
                            <h6 className="fw-normal mt-2 mb-3">
                              {address.phone}
                            </h6>

                            <span className="text-muted">
                              <i className="fa-regular fa-address-book text-muted me-1" />
                              {t("AddressLabel")}
                            </span>
                            <h6 className="fw-normal mt-2 mb-3">
                              {address.address1} , {address.address2}
                            </h6>

                            <span className="text-muted">
                              <i className="fa-solid fa-globe me-1" />{" "}
                              {t("LocationLabel")}
                            </span>
                            <h6 className="fw-normal mt-2 mb-4">
                              {address.country} / {address.city} /{" "}
                              {address.state}
                            </h6>

                            <hr />

                            <div className="d-flex justify-content-between">
                              <h5 className="cardtitlecolor">
                                {t("TotalLabel")}
                              </h5>
                              <h5 className="cardtitlecolor">
                                {fmt(order.total_price, order.currency)}
                              </h5>
                            </div>
                          </div>

                          <div className="col-lg-6">
                            <h5 className="mb-4 cardtitlecolor">
                              {t("OrderSummaryTitle")}
                            </h5>

                            <div className="row mb-4">
                              <div className="col-lg-4">
                                <p className="text-muted mb-1 text-start">
                                  <i className="fa-regular fa-calendar text-muted me-1" />
                                  {t("OrderDate")}
                                </p>
                                <h5 className="fw-normal text-start">
                                  {formatDateTime(order.order_date)}
                                </h5>
                              </div>

                              <div className="col-lg-4 col-md-4 col-sm-6 col-12 text-start">
                                <p className="text-muted mb-1 ">
                                  <i className="fa-regular fa-clipboard text-muted me-1" />
                                  {t("OrderID")}
                                </p>
                                <h5 className="fw-normal">#{order.id}</h5>
                              </div>

                              <div className="col-lg-4 col-md-4 col-sm-6 col-12 text-start">
                                <p className="text-muted mb-1">
                                  <i className="fas fa-project-diagram text-muted me-1" />
                                  {t("StatusLabel")}
                                </p>
                                <span
                                  className="badge text-white fw-light p-2 mb-1 pe-3 px-3"
                                  style={{
                                    fontSize: 13,
                                    backgroundColor: "var(--cherry-color)",
                                  }}
                                >
                                  {order.status}
                                </span>
                              </div>
                            </div>

                            {order.items.map((item) => (
                              <div
                                key={item.id}
                                className="cardborder rounded-3 p-3 mb-2 w-100"
                              >
                                {/* Desktop */}
                                <div className="d-none d-sm-block">
                                  <div className="d-flex align-items-center gap-3">
                                    <img
                                      src={
                                        item.image_url ||
                                        "https://mukemel.com/wp-content/uploads/2025/04/BTC0164-1200x1200.webp"
                                      }
                                      width={80}
                                      height={80}
                                      className="object-fit-cover"
                                      alt={item.product_name}
                                    />

                                    <div className="d-flex justify-content-between align-items-center flex-grow-1">
                                      <div>
                                        <h6 className="cardtitlecolor fw-semibold mb-1">
                                          {item.product_name}
                                        </h6>
                                        <div className="small text-muted">
                                          <span className="fw-14 me-1">
                                            {t("ColorLabel")} :{" "}
                                            {localizedColor(item)}
                                          </span>{" "}
                                          |{" "}
                                          <span className="mx-1 fw-14">
                                            {t("QuantityLabel")} :{" "}
                                            {item.quantity}
                                          </span>
                                        </div>
                                      </div>

                                      <div>
                                        <h5 className="mb-0 fw-semibold cardtitlecolor">
                                          {/* {fmt(
                                            pickLineAmount(item, order),
                                            order.currency
                                          )}{" "} */}
                                          {formatMoney(
                                            pickLineAmount(
                                              item,
                                              order,
                                              order.items
                                            ),
                                            order.currency
                                          )}
                                        </h5>
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                {/* Mobile */}
                                <div className="d-block d-sm-none">
                                  <div className="col-lg-12">
                                    <img
                                      src={
                                        item.image_url ||
                                        "https://mukemel.com/wp-content/uploads/2025/04/BTC0164-1200x1200.webp"
                                      }
                                      width={80}
                                      height={80}
                                      className="object-fit-cover"
                                      alt={item.product_name}
                                    />
                                    <h6 className="cardtitlecolor fw-semibold mb-1 mt-2">
                                      {item.product_name}
                                    </h6>
                                    <div className="small text-muted mb-1">
                                      <span className="fw-14 me-1 ">
                                        {t("ColorLabel")} :{" "}
                                        {localizedColor(item)}
                                      </span>{" "}
                                      |{" "}
                                      <span className="mx-1 fw-14">
                                        {t("QuantityLabel")} : {item.quantity}
                                      </span>
                                    </div>
                                    <h5 className="mb-0 fw-semibold cardtitlecolor">
                                      {fmt(
                                        pickLineAmount(item, order),
                                        order.currency
                                      )}
                                    </h5>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                          <div className="col-lg-12">
                            {couponUsed ? (
                              <div className="alert alert-success mt-3 mb-0 p-3">
                                <small>
                                  {t("couponalert1")}{" "}
                                  <strong className="text-uppercase">
                                    {couponCode}
                                  </strong>{" "}
                                  {t("couponalert2")}{" "}
                                  <strong>
                                    {formatMoney(
                                      couponDiscount,
                                      order.currency
                                    )}
                                  </strong>
                                </small>
                              </div>
                            ) : (
                              <div className="alert alert-danger mt-3 mb-0 p-3">
                                <small>{t("couponalert3")}</small>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="alert alert-danger py-4 text-center" role="alert">
                <h5 className="fw-bold">{t("NoOrdersAlertTitle")}</h5>
                <p>{t("NoOrdersAlertMessage")}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default UserOrders;
